<template>
  <LayoutPage title="Notifications">
    <DialogViewTask
      v-if="isOpenViewTask"
      v-model="isOpenViewTask"
      :task-id="selectedTaskId"
      @task-updated="onTaskResolved"
    />

    <DialogBulkOperationDetails
      v-if="selectedBulkNotification"
      v-model="showBulkDetailsDialog"
      :bulk-notification="selectedBulkNotification"
      @approve="handleApproveBulkOperation"
      @reject="handleRejectBulkOperation"
      @close="handleCloseBulkOperation"
    />

    <v-tabs v-model="tab">
      <v-tab
        v-for="user in notificationUsers"
        :key="user.id"
        :title="`Username: ${user.username}; Total unread: ${
          totalUnreadUserNotifications[user.id]
        }`"
      >
        {{ user.firstName }} {{ user.lastName }}
        <span v-if="currentUserId == user.id" class="pl-2">(me)</span>
        <span v-if="!loading"
          >:
          {{
            currentUserId == user.id
              ? currentUserUnreadTotal
              : totalUnreadUserNotifications[user.id]
          }}</span
        >
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item v-for="user in notificationUsers" :key="user.id">
        <div
          v-if="currentUserId == user.id && notifications.length > 0"
          class="d-flex justify-end align-center mb-2"
        >
          <span
            v-if="!hasDismissableNotifications"
            class="text--secondary text-caption mr-3"
          >
            The remaining notifications require action and cannot be dismissed
          </span>
          <v-btn
            color="primary"
            :loading="isDismissingAll"
            :disabled="!hasDismissableNotifications"
            @click="dismissAll"
          >
            Dismiss All
          </v-btn>
        </div>

        <v-data-table
          :headers="[
            {
              text: 'Notification',
              value: 'summary',
              width: '50%',
            },
            {
              text: 'Created',
              value: 'insertedAt',
              class: 'text-no-wrap',
            },
            {
              text: 'Actions',
              value: 'actions',
              sortable: false,
              filterable: false,
            },
          ]"
          :items="
            user.id === currentUserId
              ? notifications
              : userNotifications[user.id]
          "
          :search="search"
          :sort-by="['insertedAt']"
          :sort-desc="[true]"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          :loading="loading"
        >
          <template #body="{ items }">
            <tbody v-if="loading">
              <tr>
                <td colspan="3" class="text-center grey--text text--darken-2">
                  Loading user notifications
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="items.length === 0">
              <tr>
                <td colspan="3" class="text-center grey--text text--darken-2">
                  {{
                    user.id === currentUserId
                      ? "You are"
                      : `${user.firstName} is`
                  }}
                  all caught up! No notifications found.
                </td>
              </tr>
            </tbody>
            <tbody is="transition-group" v-else name="list">
              <tr
                v-for="item in items"
                :key="item.id"
                class="item-row"
                :class="`notification-${item.id}`"
                @click="toggleNotification(item)"
              >
                <td>
                  <div v-if="item.isBulkOperation" class="notification-title">
                    <div class="d-flex align-start">
                      <v-icon class="circle-icon" left small color="warning">
                        mdi-message-arrow-right
                      </v-icon>
                      <span class="font-weight-medium">
                        {{
                          item.title ||
                          (item.name === "bulk_workflow_approval"
                            ? "Approval Pending: Bulk Tenant Operations Detected."
                            : getBulkOperationSummary(item))
                        }}
                      </span>
                    </div>
                  </div>

                  <div v-else class="notification-title">
                    <div class="d-flex align-start">
                      <v-icon
                        class="circle-icon"
                        left
                        small
                        :color="severityColor(item.severity)"
                      >
                        mdi-circle
                      </v-icon>
                      <span :class="item.status">
                        {{ item.title || templateLabel(item.name) }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="
                      item.isBulkOperation && selectedNotification === item.id
                    "
                    class="notification-text"
                  >
                    <div v-if="item.body">
                      {{ item.body }}
                    </div>
                    <div v-else-if="item.notes || item.bulkNotification?.notes">
                      {{ item.notes || item.bulkNotification?.notes }}
                    </div>
                  </div>

                  <div
                    v-if="
                      !item.isBulkOperation && selectedNotification === item.id
                    "
                    class="notification-text"
                  >
                    {{ item.body }}
                  </div>
                </td>

                <td class="action-cell">
                  <DialogNotificationHistory
                    :notification-id="item.id"
                    class="mr-2"
                  />
                  <span :class="item.status">
                    {{
                      facilityFormatDateTime4Humans(
                        item.insertedAt,
                        false,
                        false
                      )
                    }}
                  </span>
                </td>

                <td class="action-cell">
                  <div v-if="item.isBulkOperation">
                    <v-btn
                      small
                      color="primary"
                      outlined
                      class="mr-2"
                      @click.stop="openBulkOperationDetails(item)"
                    >
                      View Details
                    </v-btn>
                  </div>

                  <template v-else>
                    <div
                      v-if="item.name === 'vacant_smart_unit_motion_incident'"
                    >
                      <v-menu v-model="menuStates[item.id]" offset-y>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            class="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click.stop="
                              toggleMenu(
                                item.id,
                                getFilteredEntityId(item.triggeringEntities)
                              )
                            "
                          >
                            Select Reason
                          </v-btn>
                        </template>

                        <v-list>
                          <v-list-item @click="selectOption('unit_cleaning')">
                            <v-list-item-title>Unit Cleaning</v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            @click="selectOption('suspicious_motion')"
                          >
                            <v-list-item-title
                              >Suspicious Motion</v-list-item-title
                            >
                          </v-list-item>
                          <v-list-item
                            @click="selectOption('prospective_tenant')"
                          >
                            <v-list-item-title
                              >Prospective Tenant</v-list-item-title
                            >
                          </v-list-item>
                          <v-list-item
                            @click.stop.prevent="openOtherReasonDialog"
                          >
                            <v-list-item-title>Other Reason</v-list-item-title>
                          </v-list-item>
                        </v-list>
                        <v-dialog v-model="dialog" max-width="700px">
                          <v-card>
                            <v-card-title>
                              <span class="headline"
                                >Specify other reasons there was a motion in a
                                unit</span
                              >
                              <v-spacer />
                              <v-btn icon @click="closeDialog">
                                <v-icon>mdi-close</v-icon>
                              </v-btn>
                            </v-card-title>
                            <div class="pa-4">
                              <AlertsAnimated :alerts="localAlerts" />
                            </div>
                            <v-card-text>
                              <div class="mt-4">
                                <span class="subtitle-1"
                                  >Authorization Status</span
                                >
                                <v-select
                                  v-model="authorizationStatus"
                                  :items="authorizationOptions"
                                  item-text="text"
                                  item-value="value"
                                  label="Select Authorization Status"
                                  outlined
                                  class="mt-2"
                                />
                              </div>

                              <v-textarea
                                v-model="otherReason"
                                label="Enter your reason here"
                                rows="4"
                                outlined
                                class="mt-4"
                              />
                            </v-card-text>
                            <v-card-actions>
                              <v-spacer />
                              <v-btn color="primary" @click="addOtherReason"
                                >Add Reason</v-btn
                              >
                            </v-card-actions>
                          </v-card>
                        </v-dialog>
                      </v-menu>
                    </div>

                    <!-- <div v-if="item.name == 'task_assigned'">
                      <v-btn
                        small
                        class="primary"
                        @click="openTaskDialog(item.triggeringEntities)"
                      >
                        View Task
                      </v-btn>
                    </div> -->

                    <NotificationActionButtons
                      :notification="item"
                      :is-for-current-user="user.id === currentUserId"
                      @redirect="redirect"
                      @dismiss="dismiss"
                      @open-task-dialog="openTaskDialog"
                    />
                  </template>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>
  </LayoutPage>
</template>

<script>
import LayoutPage from "@layout/LayoutPage.vue";
import NotificationActionButtons from "./NotificationActionButtons.vue";
import DialogNotificationHistory from "./DialogNotificationHistory.vue";
import DialogBulkOperationDetails from "./DialogBulkOperationDetails.vue";

import { severityColor, templateLabel, viewNotification } from "./helper";
import { titleCase } from "@tod-ui/helpers/strings";
import useFacility from "@components/authentication/useFacility";
import useUser from "@components/authentication/useUser";
import useNotifications from "./useNotifications";
import { ref, computed } from "vue";
import { useRoute } from "vue-router/composables";
import useIncident from "@components/incident/useIncident";
import useAlerts from "@tod-ui/composables/useAlerts";
import DialogViewTask from "../manage_tasks/DialogViewTask.vue";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";

export default {
  name: "PageNotifications",
  components: {
    LayoutPage,
    DialogViewTask,
    NotificationActionButtons,
    DialogNotificationHistory,
    DialogBulkOperationDetails,
    AlertsAnimated,
  },

  setup() {
    const { fetchIncident, updateIncident } = useIncident();
    const { localAlerts, addAlert, clearAllAlerts, addLocalAlert } =
      useAlerts();

    const { facilityFormatDateTime4Humans, facilityUsers, fetchFacilityUsers } =
      useFacility();
    const {
      notifications,
      unreadNotifications,
      userNotifications,
      totalUnreadUserNotifications,
      loading,
      fetchNotifications,
      markNotificationRead,
      redirectAndMarkAsRead,
      dismissNotification,
      dismissAllNotifications,
      approveBulkOperation,
      rejectBulkOperation,
      onTaskResolved,
    } = useNotifications();

    const currentUserUnreadTotal = computed(
      () => unreadNotifications.value.length
    );
    const { currentUser, canApproveBulkOperations, canApproveFacility } =
      useUser();
    const currentUserId = computed(() => currentUser.value?.id);

    const formatBulkOperationSummary = (bulkNotification) => {
      const count = bulkNotification.notifications?.length || 0;
      const facilityName =
        bulkNotification.notifications?.[0]?.organization?.name ||
        "Unknown Facility";

      let operationType = "Bulk Operation";
      if (bulkNotification.name) {
        if (bulkNotification.name.includes("move_in")) {
          operationType = "Move-In";
        } else if (bulkNotification.name.includes("move_out")) {
          operationType = "Move-Out";
        } else if (bulkNotification.name.includes("mixed")) {
          operationType = "Mixed Operations";
        }
      }

      return `Approval Pending: Bulk Tenant Operations ${operationType}: ${count} unit${
        count !== 1 ? "s" : ""
      } Detected`;
    };

    const notificationUsers = computed(() => [
      currentUser.value,
      ...facilityUsers.value.filter((u) => u.id !== currentUserId.value),
    ]);
    const selectedTaskId = ref({});
    const selectedBulkNotification = ref(null);
    const showBulkDetailsDialog = ref(false);

    const $route = useRoute();
    const isOpenViewTask = ref(false);

    const tab = ref();
    const search = ref("");
    const selectedNotification = ref($route?.params?.id);
    const survey = ref({
      vacantUnitSurveyReason: null,
      authorizationStatus: null,
    });

    const isDismissingAll = ref(false);

    const hasDismissableNotifications = computed(() =>
      notifications.value.some((n) => n.isDismissible)
    );

    return {
      facilityFormatDateTime4Humans,
      notifications,
      currentUserUnreadTotal,
      userNotifications,
      totalUnreadUserNotifications,
      loading,
      isDismissingAll,
      hasDismissableNotifications,
      tab,
      search,
      notificationUsers,
      currentUserId,
      canApproveBulkOperations,
      canApproveFacility,
      isOpenViewTask,
      selectedTaskId,
      selectedBulkNotification,
      showBulkDetailsDialog,
      selectedNotification,
      fetchNotifications,
      markNotificationRead,
      fetchFacilityUsers,
      redirectAndMarkAsRead,
      dismissNotification,
      dismissAllNotifications,
      onTaskResolved,
      survey,
      fetchIncident,
      updateIncident,
      addAlert,
      addLocalAlert,
      localAlerts,
      approveBulkOperation,
      rejectBulkOperation,
      formatBulkOperationSummary,
      getBulkOperationType: (notification) => {
        const bulkNotif = notification.bulkNotification || notification;
        const name = bulkNotif.name || notification.name;

        if (name?.includes("move_in")) {
          return "Move-In Operations";
        } else if (name?.includes("move_out")) {
          return "Move-Out Operations";
        } else if (name?.includes("mixed")) {
          return "Mixed Operations (Move-In/Move-Out)";
        }

        return "Bulk Operations";
      },
      getBulkOperationSummary: (notification) => {
        if (notification.name === "mixed_operations") {
          return "Approval Pending: Mixed Tenant Operations Detected";
        } else if (notification.name === "bulk_operations_mixed_operations") {
          const count = notification.notifications?.length || 1;
          return `Approval Pending: Mixed Tenant Operations - ${count} units detected`;
        } else if (notification.name === "bulk_operations_move_in") {
          const count = notification.notifications?.length || 1;
          return `Approval Pending: Move-In Operations - ${count} units detected`;
        } else if (notification.name === "bulk_operations_move_out") {
          const count = notification.notifications?.length || 1;
          return `Approval Pending: Move-Out Operations - ${count} units detected`;
        } else if (notification.name?.includes("move_in_completed")) {
          return "Approval Pending: Move-In Operations Detected";
        } else if (notification.name?.includes("move_out_completed")) {
          return "Approval Pending: Move-Out Operations Detected";
        }
        return (
          notification.notes ||
          notification.title ||
          (notification.name
            ? `Bulk Operation: ${notification.name}`
            : "Bulk Operation")
        );
      },
      openBulkOperationDetails: (notification) => {
        const bulkNotification =
          notification.isBulkOperation && notification.bulkNotification
            ? notification.bulkNotification
            : notification;

        selectedBulkNotification.value = bulkNotification;
        showBulkDetailsDialog.value = true;
      },
    };
  },
  data() {
    return {
      menuStates: {},
      dialog: false,
      otherReason: "",
      authorizationStatus: null,
      authorizationOptions: [
        { text: "Authorized", value: "authorized" },
        { text: "Unauthorized", value: "un_authorized" },
      ],
    };
  },
  watch: {
    notificationUsers: {
      handler(users) {
        users.forEach((u) => {
          if (u.id !== this.currentUserId)
            this.fetchNotifications(this.$apollo, u.id);
        });
      },
      immediate: true,
    },
    selectedNotification(notificationId) {
      if (notificationId) {
        this.$vuetify.goTo(`.notification-${notificationId}`, {
          offset: 60,
        });
      }
    },
  },
  mounted() {
    this.fetchFacilityUsers(this.$apollo);
  },
  methods: {
    getFilteredEntityId(entities) {
      const filteredEntity = entities
        .map((entity) => JSON.parse(entity))
        .find(
          (entity) => entity.type === "motion" || entity.type === "incident"
        );
      return filteredEntity ? filteredEntity.id : null;
    },
    async resolveIncident() {
      try {
        // Call the submitIncidentSurvey function with the updated survey
        const response = await this.updateIncident(this.$apollo, this.survey);

        if (response) {
          this.addAlert({
            type: "success",
            message: "Incident resolved with a completed survey",
            timeout: 15,
          });
          this.fetchNotifications(this.$apollo, this.currentUserId);
        }
      } catch (error) {
        this.addAlert({
          type: "error",
          message: "Error occurred while saving incident survey.",
          timeout: 10,
        });
      }
    },

    clear() {
      this.survey = { vacantUnitSurveyReason: null, authorizationStatus: null };
      this.$refs.validator.reset();
    },
    toggleMenu(itemId, incidentId) {
      this.fetchIncident(this.$apollo, incidentId);
      this.$set(this.menuStates, itemId, !this.menuStates[itemId]);
    },

    selectOption(reason) {
      this.survey.vacantUnitSurveyReason = reason;

      if (reason === "unit_cleaning" || reason === "prospective_tenant") {
        this.authorizationStatus = "authorized";
      } else if (reason === "suspicious_motion") {
        this.authorizationStatus = "un_authorized";
      }
      this.survey.authorizationStatus = this.authorizationStatus;

      this.resolveIncident();
      this.menuStates = {};
    },

    async handleApproveBulkOperation(bulkNotification) {
      this.$set(bulkNotification, "approving", true);

      try {
        await this.approveBulkOperation(this.$apollo, bulkNotification);
        this.addAlert({
          type: "success",
          message: `Bulk operation approved successfully and individual notifications will be sent to tenants.`,
          timeout: 15,
        });
        this.fetchNotifications(this.$apollo);
        this.notificationUsers.forEach((user) => {
          if (user.id !== this.currentUserId) {
            this.fetchNotifications(this.$apollo, user.id);
          }
        });

        setTimeout(() => {
          this.fetchNotifications(this.$apollo);
          this.notificationUsers.forEach((user) => {
            if (user.id !== this.currentUserId) {
              this.fetchNotifications(this.$apollo, user.id);
            }
          });
        }, 2000);

        this.showBulkDetailsDialog = false;
      } catch (error) {
        console.error(
          "[PageNotifications] Error approving bulk operation:",
          error
        );
        this.addAlert({
          type: "error",
          message: "Failed to approve bulk operation. Please try again.",
          timeout: 10,
        });
      } finally {
        this.$set(bulkNotification, "approving", false);
      }
    },

    async handleRejectBulkOperation(bulkNotification, reason = null) {
      this.$set(bulkNotification, "rejecting", true);

      try {
        await this.rejectBulkOperation(this.$apollo, bulkNotification, reason);

        const notificationCount = bulkNotification.notifications?.length || 0;
        let message = `Bulk operation rejected successfully. ${notificationCount} associated notification${
          notificationCount !== 1 ? "s" : ""
        } will not be sent to tenants and have been dismissed.`;
        if (reason) {
          message += ` Reason: ${reason}`;
        }

        this.addAlert({
          type: "success",
          message,
          timeout: 15,
        });

        this.fetchNotifications(this.$apollo);
        this.showBulkDetailsDialog = false;
      } catch (error) {
        console.error("Error rejecting bulk operation:", error);
        this.addAlert({
          type: "error",
          message: "Failed to reject bulk operation. Please try again.",
          timeout: 10,
        });
      } finally {
        this.$set(bulkNotification, "rejecting", false);
      }
    },

    handleCloseBulkOperation() {
      this.showBulkDetailsDialog = false;
    },

    openOtherReasonDialog() {
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.otherReason = "";
    },
    addOtherReason() {
      if (!this.otherReason.trim()) {
        this.addLocalAlert({
          type: "error",
          message: "Please enter a valid reason before proceeding.",
          timeout: 10,
        });
        return;
      }
      this.selectOption(this.otherReason);
      this.closeDialog();
    },
    openTaskDialog(notification) {
      const firstEntity = JSON.parse(notification.triggeringEntities[0]);
      this.isOpenViewTask = true;
      return (this.selectedTaskId = firstEntity.id);
    },
    async toggleNotification(notification) {
      if (notification.id === this.selectedNotification) {
        this.selectedNotification = null;
        this.$router.replace({ name: "Notifications" });
      } else {
        this.selectedNotification = notification.id;
        this.markNotificationRead(this.$apollo, notification);
        this.$router.replace(viewNotification(notification));
      }
    },
    async redirect(notification) {
      await this.redirectAndMarkAsRead(this.$apollo, notification);
    },
    async dismiss(notification) {
      await this.dismissNotification(this.$apollo, notification);
    },
    async dismissAll() {
      this.isDismissingAll = true;
      let response = await this.dismissAllNotifications(this.$apollo);
      this.isDismissingAll = false;
      if (response) {
        this.addAlert({
          type: "success",
          message: `Dismissed ${response.data?.dismissNotifications} notifications`,
          timeout: 10,
        });
      } else {
        this.addAlert({
          type: "error",
          message: "Failed to dismiss notifications. Please try again.",
          timeout: 10,
        });
      }
    },
    severityColor,
    templateLabel,
    titleCase,
    getBulkStatusColor(status) {
      switch (status) {
        case "published":
          return "orange";
        case "resolved":
          return "green";
        case "dismissed":
          return "grey";
        default:
          return "orange";
      }
    },
    getBulkStatusText(status) {
      switch (status) {
        case "published":
          return "Waiting for Approval";
        case "resolved":
          return "Approved";
        case "dismissed":
          return "Rejected";
        default:
          return "Waiting for Approval";
      }
    },
  },
};
</script>

<style scoped>
.list-enter-active,
.list-leave-active {
  transition: all 0.8s;
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateX(50%);
}

.list-move {
  transition: transform 0.5s;
}

.item-row {
  display: table-row;
}

.notification-link {
  text-decoration: none;
  color: currentColor;
}

.published {
  font-weight: bold;
}

.action-cell {
  vertical-align: top;
  padding-top: 10px !important;
}

.circle-icon {
  line-height: 30px;
}

.notification-title {
  line-height: 30px;
  cursor: pointer;
}

.notification-text {
  min-height: 48px;
  padding-left: 2em;
  color: gray;
}
</style>
